import {
  JsonFormat,
  numberJsonFormat,
  objectJsonFormat,
} from "@redotech/json/format";
import { bijectionCompose, bijectionIdentity } from "@redotech/util/bijection";
import { Store } from "@redotech/util/store";
import { jsonStore, localStore } from "@redotech/web-util/storage";

/**
 * Optionally expiring value.
 */
interface Expiring {
  expiry?: Date;
  value: any;
}

const expiringJsonFormat: JsonFormat<Expiring> = objectJsonFormat(
  {},
  {
    expiry: bijectionCompose(numberJsonFormat, {
      read(milliseconds) {
        return new Date(milliseconds);
      },
      write(date) {
        return +date;
      },
    }),
    value: bijectionIdentity(),
  },
);

function expiringLocalStore(key: string): Store<Expiring | undefined> {
  return jsonStore(localStore(key), expiringJsonFormat);
}

export function setLocalStorageJson(key: string, value: any) {
  expiringLocalStore(key).set({ value });
}

export function getLocalStorageJson(key: string): any {
  const item = expiringLocalStore(key).get();
  return item !== undefined ? item.value : null;
}

export function setLocalStorageWithExpiry(
  key: string,
  value: any,
  numHours: number = 1,
) {
  const ttl = 60 * 60 * 1000 * numHours;
  const now = new Date();
  const item = { value, expiry: new Date(now.getTime() + ttl) };
  expiringLocalStore(key).set(item);
}

export function setLocalStorageWithExpirySeconds(
  key: string,
  value: any,
  numSeconds: number = 1,
) {
  const ttl = 1000 * numSeconds;
  const now = new Date();
  const item = { value, expiry: new Date(now.getTime() + ttl) };
  expiringLocalStore(key).set(item);
}

export interface GetLocalStorageWithExpiryOptions {
  returnNullIfMissingExpiry: boolean;
}

export function getLocalStorageWithExpiry(
  key: string,
  options: GetLocalStorageWithExpiryOptions = {
    returnNullIfMissingExpiry: false,
  },
): any {
  const item = expiringLocalStore(key).get();
  if (item === undefined) {
    return null;
  }
  const now = new Date();
  if (item.expiry ? item.expiry < now : options.returnNullIfMissingExpiry) {
    return null;
  }
  return item.value;
}

export function removeItemFromLocalStorage(key: string) {
  localStore(key).set(undefined);
}
